import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiLock, FiMail } from 'react-icons/fi';
import { useAuth } from '../auth/AuthContext';
import SocialSignIn from '../components/SocialSignIn';
import { MFA_MESSAGE, safeDestination } from '../auth/oauth';
import './LoginPage.css';

type Mode = 'login' | 'register';

export default function LoginPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation() as { state?: { from?: string; mode?: Mode } };
  const redirectTo = safeDestination(location.state?.from);

  const [mode, setMode] = useState<Mode>(location.state?.mode === 'register' ? 'register' : 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(
    new URLSearchParams(window.location.search).get('verified') === '1'
      ? 'Email verified. Sign in with the password you just set.'
      : null,
  );
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      if (mode === 'login') {
        const session = await login(email.trim(), password);
        if (session.requiresMfa) {
          setError(MFA_MESSAGE);
          return;
        }
        navigate(redirectTo, { replace: true });
      } else {
        const registration = await register({ email: email.trim(), firstName: firstName.trim(), lastName: lastName.trim() });
        setNotice(registration.message || 'Check your email for next steps.');
        setMode('login');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth">
      <div className="auth__card">
        <div className="auth__brand">
          <span className="auth__logo" aria-hidden>
            🎙️
          </span>
          <span className="auth__brandName">DocuCast</span>
        </div>
        <p className="auth__tagline">Turn your documents into podcasts, audiobooks, and read-aloud audio.</p>

        <div className="auth__tabs">
          <button className={`auth__tab${mode === 'login' ? ' auth__tab--active' : ''}`} onClick={() => setMode('login')}>
            Sign in
          </button>
          <button
            className={`auth__tab${mode === 'register' ? ' auth__tab--active' : ''}`}
            onClick={() => setMode('register')}
          >
            Create account
          </button>
        </div>

        <form className="auth__form" onSubmit={submit}>
          {mode === 'register' && (
            <div className="auth__row">
              <div className="field">
                <label className="field__label" htmlFor="fn">
                  First name
                </label>
                <input id="fn" className="input" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              </div>
              <div className="field">
                <label className="field__label" htmlFor="ln">
                  Last name
                </label>
                <input id="ln" className="input" value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </div>
            </div>
          )}

          <div className="field">
            <label className="field__label" htmlFor="email">
              Email
            </label>
            <div className="auth__inputIcon">
              <FiMail />
              <input
                id="email"
                className="input"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
              />
            </div>
          </div>

          {mode === 'login' && <div className="field">
            <label className="field__label" htmlFor="password">
              Password
            </label>
            <div className="auth__inputIcon">
              <FiLock />
              <input
                id="password"
                className="input"
                type="password"
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
              />
            </div>
          </div>}

          {error && <div className="auth__error">{error}</div>}
          {notice && <div className="auth__notice">{notice}</div>}

          <button className="btn btn--primary btn--block btn--lg" type="submit" disabled={busy}>
            {busy ? <span className="spinner spinner--sm" /> : mode === 'login' ? 'Sign in' : 'Create account'}
          </button>
        </form>

        <div className="auth__divider"><span>or</span></div>
        <SocialSignIn destination={redirectTo} disabled={busy} />
      </div>
    </div>
  );
}
